import { Identify } from '@amplitude/analytics-browser';
import { AnalyticsService } from './analytics';
import { HttpService } from './http';
import { UserService, languageCodes } from './user';
export class AuthService {
  static async getAndStoreUser({
    authorizationToken,
    userId
  }, remember = false) {
    const user = await UserService.getUser(userId);
    if (!user) throw new Error('user not found');
    UserService.currentUser = user;
    try {
      const {
        id,
        isGeovelo,
        isEcoCompteur
      } = UserService.currentUser;
      const event = new Identify();
      event.set('user_id', id);
      event.set('is_staff', isGeovelo || isEcoCompteur);
      AnalyticsService.identify(event);
    } catch (err) {
      console.error(err);
    }
    try {
      if (remember) {
        localStorage.setItem('authorization_token', authorizationToken);
        localStorage.setItem('user_id', `${userId}`);
      } else {
        sessionStorage.setItem('authorization_token', authorizationToken);
        sessionStorage.setItem('user_id', `${userId}`);
      }
    } catch {
      console.error('localStorage access is denied');
    }
    return user;
  }
  static removeStoredUser() {
    try {
      localStorage.removeItem('impersonate_authorization_token');
      localStorage.removeItem('impersonate_user_id');
      localStorage.removeItem('authorization_token');
      localStorage.removeItem('user_id');
      sessionStorage.removeItem('authorization_token');
      sessionStorage.removeItem('user_id');
    } catch {
      console.error('localStorage access is denied');
    }
  }
  static async signIn({
    provider,
    disableSSOSignUp,
    token,
    language,
    remember,
    expectedStatus
  }) {
    try {
      const data = await HttpService.post('v1', `/authentication/${provider}`, disableSSOSignUp ? [{
        key: 'disable_user_creation',
        value: true
      }] : [], [{
        key: 'Authentication',
        value: token
      }, {
        key: 'Accept-Language',
        value: languageCodes[language]
      }], null, {
        setAuthorizationToken: true,
        expectedStatus
      });
      return AuthService.getAndStoreUser(data, remember);
    } catch (err) {
      AuthService.removeStoredUser();
      throw err;
    }
  }
  static async register(email, password, language) {
    try {
      const data = await HttpService.post('v2', '/register', [], [{
        key: 'Accept-Language',
        value: languageCodes[language]
      }], JSON.stringify({
        email,
        password
      }), {
        setAuthorizationToken: true
      });
      return AuthService.getAndStoreUser(data);
    } catch (err) {
      console.error('[AuthService][register]', err);
      AuthService.removeStoredUser();
      throw err;
    }
  }
  static async sendResetPasswordEmail(email) {
    try {
      await HttpService.post('v2', '/password/reset_token', [], [], JSON.stringify({
        email
      }));
      return true;
    } catch (err) {
      console.error('[AuthService][sendResetPasswordEmail]', err);
      throw err;
    }
  }
  static async resetPassword(_token, password) {
    try {
      const token = btoa(encodeURIComponent(_token + ';' + password).replace(/%([0-9A-F]{2})/g, (...args) => String.fromCharCode(parseInt(`0x${args[1]}`, 16))));
      await HttpService.post('v2', '/password/reset', [], [{
        key: 'Authentication',
        value: token
      }]);
      return true;
    } catch (err) {
      console.error('[AuthService][resetPassword]', err);
      throw err;
    }
  }
  static async signInWithEmail(email, password, remember = true, language) {
    try {
      if (!email || !password) throw new Error('missing email or password');
      const token = btoa(encodeURIComponent(email + ';' + password).replace(/%([0-9A-F]{2})/g, (...args) => String.fromCharCode(parseInt(`0x${args[1]}`, 16))));
      const user = await this.signIn({
        provider: 'geovelo',
        token,
        language,
        remember,
        expectedStatus: 200
      });
      return user;
    } catch (err) {
      console.error('[AuthService][signInWithEmail]', err);
      throw err;
    }
  }
  static async signInWithProvider(provider, token, remember = true, language, disableSSOSignUp) {
    try {
      const user = await this.signIn({
        provider,
        token,
        language,
        remember,
        disableSSOSignUp
      });
      return user;
    } catch (err) {
      console.error('[AuthService][signInWithProvider]', err);
      throw err;
    }
  }
  static async generateOtpEmailCode(email) {
    try {
      await HttpService.post('v1', '/authentication/otp_email/code_generation', [], [], JSON.stringify({
        email
      }), {
        expectedStatus: 201
      });
      return true;
    } catch (err) {
      console.error('[AuthService][generateOtpEmailCode]', err);
      throw err;
    }
  }
  static async signInWithOtpEmail(email, otp, remember = true) {
    try {
      if (!email || !otp) throw new Error('missing email or otp');
      const data = await HttpService.post('v1', '/authentication/otp_email/code_verification', [], [], JSON.stringify({
        email,
        otp
      }), {
        setAuthorizationToken: true,
        expectedStatus: 200
      });
      return AuthService.getAndStoreUser(data, remember);
    } catch (err) {
      console.error('[AuthService][signInWithOtpEmail]', err);
      AuthService.removeStoredUser();
      throw err;
    }
  }
  static async signOut() {
    try {
      await HttpService.delete('v1', '/authentication');
      try {
        localStorage.removeItem('impersonate_authorization_token');
        localStorage.removeItem('impersonate_user_id');
        localStorage.removeItem('authorization_token');
        localStorage.removeItem('user_id');
        sessionStorage.removeItem('authorization_token');
        sessionStorage.removeItem('user_id');
      } catch {
        console.error('localStorage access is denied');
      }
      HttpService.authorizationToken = null;
      UserService.currentUser = null;
      try {
        const event = new Identify();
        AnalyticsService.identify(event);
      } catch (err) {
        console.error(err);
      }
      if ('google' in window) google.accounts.id.disableAutoSelect();
      return true;
    } catch (err) {
      console.error('[AuthService][signOut]', err);
      throw err;
    }
  }
  static async authorize(clientId, codeChallenge, state) {
    try {
      const queryParams = [{
        key: 'client_id',
        value: clientId
      }, {
        key: 'response_type',
        value: 'code'
      }, {
        key: 'code_challenge_method',
        value: 'S256'
      }, {
        key: 'code_challenge',
        value: codeChallenge
      }, {
        key: 'scope',
        value: 'trace_summary_read'
      }, {
        key: 'oauth_302_redirect',
        value: false
      }];
      if (state) queryParams.push({
        key: 'state',
        value: state
      });
      const res = await HttpService.get('v1', '/oauth/authorize', queryParams);
      if (!res.redirect_to) throw new Error('no redirect url');
      return res.redirect_to;
    } catch (err) {
      console.error('[AuthService][authorize]', err);
      throw err;
    }
  }
}